/**
 * Which mod kit, siren and light ids the base game already owns, and where a
 * custom one can be put without walking into somebody else.
 *
 * A `carvariations.meta` entry points a vehicle at a mod kit by name, and
 * `carcols.meta` gives that kit a numeric id. The id is the thing the engine
 * actually keys on, and it is global: two kits that claim the same id are one
 * kit as far as the game is concerned, and which of them a player sees depends
 * on resource load order. The symptom is never an error message. The vehicle
 * spawns, the mod menu opens, and the wheels and bumpers on offer belong to
 * somebody else's car.
 *
 * The naive way to hand out an id is to walk up from 1 and take the first one
 * that is free in the file you are looking at. Every generator that has ever
 * touched a `carcols.meta` does exactly that, and it is wrong twice over:
 *
 *   1. Stock GTA occupies 614 ids between 0 and 999. A kit that takes id 12 has
 *      taken `13_ztype_modkit` away from the Z-Type, and the author will not
 *      find that out until somebody spawns one.
 *   2. Even in the gaps, the bottom of the range is where every other mod author
 *      is also allocating, because they all wrote the same loop. Two add-on car
 *      packs from two different creators collide at id 700 far more often than
 *      chance would suggest, because neither of them was ever going to pick
 *      65000.
 *
 * So allocation here runs top-down from the highest safe id in the pool, skips
 * the ids stock GTA owns, and skips the engine sentinels. That is the whole
 * idea. It costs nothing, it is invisible to the user, and it converts a class
 * of collision that takes an evening to diagnose into one that does not happen.
 *
 * The second thing this module exists for is the default kit. A vehicle with no
 * mods still names a kit, and the base game spells that name three different
 * ways across its own files — `0_default_modkit`, `0_default_kit` and the bare
 * `default_modkit`. The desktop analyzer only ever knew the first, so a
 * `carvariations.meta` written with the second reported a missing mod kit that
 * was never missing. `isDefaultModkitName` knows all three.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * It knows what the base game owns and what the caller says is taken. It does
 * not know what the rest of the server's resources own, because nothing in a
 * single resource can know that. An id from here is safe against stock GTA and
 * against the conventional bottom-up allocator; it is not a guarantee, and a
 * report should say "safe" rather than "unused".
 *
 * It also does not read the leading number off a kit name. Rockstar's own files
 * disagree with themselves there — `[1, "0_cvtgeabox_modkit"]` is real — so the
 * numeric id is canonical and the name is only ever a label.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  VANILLA_LIGHT_IDS,
  VANILLA_MODKIT_NAMES,
  VANILLA_MODKITS,
  VANILLA_SIREN_IDS,
} from "./data/vanillaModkits.v1";
import { normalizeName } from "./normalize";
import type { VanillaModkit, VanillaModkitIndex } from "./types";

// ─── Limits ─────────────────────────────────────────────────────────────────

/**
 * How many already-taken ids a caller may hand the allocator.
 *
 * The set comes from whatever the caller scanned, which in the desktop app is
 * every `carcols.meta` in a folder the user pointed at. This caps the work so a
 * folder full of generated junk cannot freeze the scan panel mid-render.
 *
 * Truncating a caller's list would ordinarily be dangerous — a dropped id is an
 * id we might hand out twice — so the scan is arranged so that reaching the cap
 * is not a realistic way to lose one. Values outside the pool are discarded
 * before they can matter, and the scan stops the moment the pool is entirely
 * spoken for, which is at most 65,535 values. Hitting 100,000 therefore means
 * the caller passed a list that is mostly repeats or mostly junk.
 */
const MAX_TAKEN_IDS = 100_000;

/**
 * How many ids one `allocateIds` call may hand back.
 *
 * Easy Meta asks for one id per vehicle in a pack, and a pack that wants more
 * than this is not a pack. The cap keeps the generator's progress bar honest.
 */
const MAX_ALLOCATION_BATCH = 512;

// ─── The stock kit index ────────────────────────────────────────────────────

/**
 * Built on first use and kept, never at import time.
 *
 * The table is 614 rows with a vehicle list on each, and the browser bundles
 * that run these checks are the same ones that have to paint a first frame. A
 * module that builds four maps as a side effect of being imported charges every
 * page for a check most sessions never run.
 */
let cachedIndex: VanillaModkitIndex | null = null;

export function buildVanillaModkitIndex(): VanillaModkitIndex {
  if (cachedIndex) return cachedIndex;

  const byId = new Map<number, VanillaModkit[]>();
  const kitNames = new Set<string>();
  const ids = new Set<number>();
  const vehicles = new Set<string>();

  // The generated table is a fixed size, so this loop needs no cap: it is not
  // user data and cannot grow between builds.
  for (const kit of VANILLA_MODKITS) {
    const [id, kitName, usedBy] = kit;
    ids.add(id);
    const existing = byId.get(id);
    if (existing) existing.push(kit);
    else byId.set(id, [kit]);
    kitNames.add(normalizeName(kitName));
    for (const vehicle of usedBy) vehicles.add(normalizeName(vehicle));
  }

  // The generator also records the second spelling a kit picks up when the base
  // game and a DLC ship it under different names. Those names own no id of
  // their own, but a mod that writes one is naming a stock kit.
  for (const kitName of VANILLA_MODKIT_NAMES) kitNames.add(normalizeName(kitName));

  cachedIndex = { byId, kitNames, ids, vehicles };
  return cachedIndex;
}

/** Whether stock GTA already defines a kit with this id. */
export function isStockModkitId(id: number): boolean {
  return buildVanillaModkitIndex().ids.has(id);
}

/**
 * The stock kits at an id, so a report can name what a collision costs.
 *
 * "Mod kit id 16 is already `9_police3_modkit`" is a sentence an author can act
 * on; "id 16 is in use" is not. Usually one row, occasionally more where the
 * base game and a DLC both wrote the id.
 */
export function stockKitsForId(id: number): VanillaModkit[] {
  const kits = buildVanillaModkitIndex().byId.get(id);
  return kits ? [...kits] : [];
}

/**
 * Whether a kit name is one the base game already uses.
 *
 * Case-insensitive, because `carcols.meta` files in the wild are not consistent
 * about it and the game does not care.
 */
export function isStockModkitName(kitName: string): boolean {
  const name = normalizeName(kitName);
  // The bare `default_modkit` spelling is not in the generated name list — the
  // game stores it hashed — but it names the stock default kit all the same,
  // and calling it custom is how the phantom "missing kit" finding happened.
  if (isDefaultModkitName(name)) return true;
  return buildVanillaModkitIndex().kitNames.has(name);
}

// ─── The default kit ────────────────────────────────────────────────────────

/**
 * The three spellings of "this vehicle has no mod kit" that appear in real
 * files.
 *
 * They are the same kit. A `carvariations.meta` naming any of them is saying the
 * vehicle takes the base game's empty kit, which is not a reference to anything
 * the resource has to ship, so no analyzer should go looking for it in
 * `carcols.meta`.
 */
const DEFAULT_MODKIT_NAMES: ReadonlySet<string> = new Set<string>([
  "0_default_modkit",
  "0_default_kit",
  "default_modkit",
]);

export function isDefaultModkitName(kitName: string): boolean {
  return DEFAULT_MODKIT_NAMES.has(normalizeName(kitName));
}

// ─── Id pools ───────────────────────────────────────────────────────────────

/**
 * The four numeric namespaces this layer hands out ids in.
 *
 * `sirenSsla` is the same `sirenSettings` field as `siren`, on a server running
 * SSLA, which widens the field the game reads it into. Asking for the wrong one
 * is not a small mistake: an id above 254 on a vanilla server does not fall back
 * to a default siren, the vehicle simply has no lights.
 */
export type IdPool = "modkit" | "siren" | "sirenSsla" | "light";

type IdPoolSpec = { min: number; max: number; sentinels: readonly number[]; label: string };

/**
 * Where each pool starts and stops, and which ids inside it are poisoned.
 *
 * `min` is 1 everywhere and never 0. Zero is not an id in any of these fields —
 * it means "none", and a kit that claims it is claiming the empty default kit
 * for every vehicle in the game.
 *
 * The sentinels are the values the engine reads as "unset" because they are the
 * all-ones pattern of the byte or the word the field is stored in. Only the ones
 * that actually land inside a given pool are listed: 255 is a real hazard in the
 * one-byte light pool and irrelevant to a siren pool that stops at 254.
 */
export const ID_POOLS: Record<IdPool, IdPoolSpec> = {
  modkit: { min: 1, max: 65535, sentinels: [255, 65535], label: "modkit" },
  siren: { min: 1, max: 254, sentinels: [], label: "siren" },
  sirenSsla: { min: 1, max: 65534, sentinels: [255], label: "SSLA siren" },
  light: { min: 1, max: 255, sentinels: [255], label: "light" },
};

/**
 * The record for a pool name, or `null` when there is no such pool.
 *
 * `IdPool` is a closed union, so this only fires when a value crossed a type
 * boundary — a pool name restored from saved UI state, a plain-JavaScript
 * caller, a field read out of a project file. Returning `null` rather than
 * letting the destructure throw matters because the caller is usually a batch
 * generator: an unrecognised pool should cost that one vehicle its id, not
 * abandon the other hundred and ninety-nine mid-run.
 */
function poolSpec(pool: IdPool): IdPoolSpec | null {
  return Object.prototype.hasOwnProperty.call(ID_POOLS, pool) ? ID_POOLS[pool] : null;
}

/** The highest id in a pool that is not a sentinel — where allocation starts. */
function highestSafeId(spec: IdPoolSpec): number {
  let candidate = spec.max;
  while (candidate >= spec.min && spec.sentinels.includes(candidate)) candidate -= 1;
  return candidate;
}

// ─── What stock GTA already owns ────────────────────────────────────────────

/**
 * The stock ids for a pool, built on first use and kept.
 *
 * Both siren pools read the same table: SSLA widens the range a server accepts,
 * it does not give the base game's profiles different numbers.
 *
 * The modkit set is copied out of the shared index rather than aliased to it.
 * `buildVanillaModkitIndex` hands its `ids` set to anyone who asks, and a caller
 * that adds its own ids to that set to reuse it as a scratch collection would
 * otherwise be editing the allocator's idea of what the base game owns for the
 * rest of the session — 614 entries copied once is cheaper than the evening that
 * would cost to find.
 */
const cachedReserved = new Map<IdPool, ReadonlySet<number>>();

function reservedStockIds(pool: IdPool): ReadonlySet<number> {
  const cached = cachedReserved.get(pool);
  if (cached) return cached;

  let built: ReadonlySet<number>;
  if (pool === "modkit") built = new Set<number>(buildVanillaModkitIndex().ids);
  else if (pool === "light") built = new Set<number>(VANILLA_LIGHT_IDS);
  else built = new Set<number>(VANILLA_SIREN_IDS);

  cachedReserved.set(pool, built);
  return built;
}

// ─── Allocation ─────────────────────────────────────────────────────────────

/**
 * The caller's taken ids as a set, with everything that cannot collide dropped.
 *
 * Callers scrape these out of XML, so a `sirenSettings` of `"abc"` arrives as
 * `NaN` and a hand-edited file arrives as `-1`. Neither of those is an id the
 * allocator could ever hand out, and neither is a modkit id of 40000 when the
 * pool being allocated from is the 254-wide siren pool, so all of it goes.
 *
 * Filtering to the pool is what keeps `MAX_TAKEN_IDS` honest. Once the set holds
 * every usable id in the pool there is nothing further to learn from the rest of
 * the list, so the scan stops there and the cap is left for input that is junk.
 *
 * Something that is not iterable at all is treated as nothing taken. A caller
 * that passes a bare number has a bug, but the id this returns is still a safe
 * one, and stopping a whole generation over it would not help them find it.
 */
function toTakenSet(taken: Iterable<number> | undefined, spec: IdPoolSpec): Set<number> {
  const set = new Set<number>();
  if (!taken || typeof (taken as Iterable<number>)[Symbol.iterator] !== "function") return set;

  const poolWidth = spec.max - spec.min + 1;
  let seen = 0;
  for (const value of taken) {
    seen += 1;
    if (seen > MAX_TAKEN_IDS) break;
    if (!Number.isInteger(value) || value < spec.min || value > spec.max) continue;
    set.add(value);
    if (set.size >= poolWidth) break;
  }
  return set;
}

/**
 * `count` safe ids for a pool, highest first.
 *
 * Fewer than `count` come back when the pool runs out, and an empty array when
 * there is nothing left at all. That is deliberately not an error: a caller
 * generating kits for a 200-car pack against a nearly-full siren pool wants to
 * be told how far it got, not to have the whole generation throw. A request for
 * more than `MAX_ALLOCATION_BATCH` is trimmed to it for the same reason, so a
 * caller that asks for a million ids gets 512 rather than a frozen tab.
 *
 * The downward scan is bounded by the pool itself — 65,534 steps at the very
 * worst, over a `Set` lookup — so it needs no cap of its own.
 */
export function allocateIds(pool: IdPool, count: number, taken?: Iterable<number>): number[] {
  const spec = poolSpec(pool);
  if (!spec) return [];

  const wanted = Number.isFinite(count) ? Math.min(Math.floor(count), MAX_ALLOCATION_BATCH) : 0;
  if (wanted <= 0) return [];

  const stock = reservedStockIds(pool);
  const used = toTakenSet(taken, spec);
  const allocated: number[] = [];

  for (let id = highestSafeId(spec); id >= spec.min; id -= 1) {
    if (spec.sentinels.includes(id)) continue;
    if (stock.has(id)) continue;
    if (used.has(id)) continue;
    allocated.push(id);
    if (allocated.length >= wanted) break;
  }
  return allocated;
}

/**
 * One safe id, or `null` when the pool has nothing left.
 *
 * `null` rather than a fallback id on purpose. Returning a colliding id and
 * hoping is how a generator produces a pack that looks fine and breaks the first
 * time it is loaded next to anything else.
 */
export function allocateId(pool: IdPool, taken?: Iterable<number>): number | null {
  const [id] = allocateIds(pool, 1, taken);
  return id === undefined ? null : id;
}

// ─── Explaining a choice ────────────────────────────────────────────────────

/** The smallest power of ten strictly above a value: 999 → 1000, 20 → 100. */
function powerOfTenAbove(value: number): number {
  let boundary = 1;
  while (boundary <= value && boundary < Number.MAX_SAFE_INTEGER / 10) boundary *= 10;
  return boundary;
}

/** The first capital of a sentence, without disturbing the rest of the label. */
function capitalize(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);
}

/**
 * What taking an id the base game already owns actually costs, per pool.
 *
 * These are three different failures and a report that describes them with one
 * sentence teaches the author the wrong lesson. Overwriting a stock mod kit
 * empties a menu; overwriting a stock siren profile changes how a police car
 * that has nothing to do with the mod flashes on every server it loads on.
 */
const SIREN_COLLISION_CONSEQUENCE =
  "so every stock emergency vehicle on that profile flashes with the custom pattern instead" +
  " of its own, on every server that loads the resource";

const STOCK_COLLISION_CONSEQUENCE: Record<IdPool, string> = {
  modkit:
    "so a custom kit that claims it replaces the base game's, and the stock vehicles built on" +
    " that kit open their mod menu to somebody else's parts",
  siren: SIREN_COLLISION_CONSEQUENCE,
  sirenSsla: SIREN_COLLISION_CONSEQUENCE,
  light:
    "so the stock vehicles that share the profile change their light colour along with the" +
    " custom one",
};

/**
 * The sentence a UI puts next to an id it chose for the user.
 *
 * An allocator that silently picks 65534 looks broken — the number is absurd,
 * and the first thing an author does is change it back to something tidy near
 * the bottom, which is precisely the mistake this module exists to prevent. So
 * the choice ships with its reason attached.
 *
 * Handed an id the allocator would not have chosen, it says why that id is not
 * safe instead of manufacturing a justification for it.
 */
export function explainAllocation(pool: IdPool, id: number): string {
  const spec = poolSpec(pool);
  if (!spec) return `There is no id pool named "${String(pool)}", so no id was assigned.`;

  const { min, max, sentinels, label } = spec;
  const name = capitalize(label);

  if (!Number.isInteger(id)) {
    return (
      `${name} id ${String(id)} is not a whole number, and the game stores this field as an` +
      ` integer, so nothing would read it back.`
    );
  }
  if (id < min || id > max) {
    return (
      `${name} id ${id} is outside the usable range ${min}-${max}, so the game cannot store it.`
    );
  }
  if (sentinels.includes(id)) {
    return (
      `${name} id ${id} is the value the engine reads as "none", so a vehicle assigned it` +
      ` behaves as though it had no ${label} at all.`
    );
  }

  const stock = reservedStockIds(pool);
  if (stock.has(id)) {
    const kits = pool === "modkit" ? stockKitsForId(id).map((kit) => kit[1]) : [];
    const owner = kits.length > 0 ? ` (${kits.join(", ")})` : "";
    return (
      `${name} id ${id} already belongs to stock GTA${owner},` +
      ` ${STOCK_COLLISION_CONSEQUENCE[pool]}.`
    );
  }

  let highestStock = 0;
  for (const stockId of stock) if (stockId > highestStock) highestStock = stockId;
  const boundary = Math.min(powerOfTenAbove(highestStock), max + 1);
  let ownedBelowBoundary = 0;
  for (const stockId of stock) if (stockId < boundary) ownedBelowBoundary += 1;

  const reason =
    `Assigned ${label} id ${id} because stock GTA owns ${ownedBelowBoundary} ids under` +
    ` ${boundary} and ids near them are where other mods allocate too.`;
  if (sentinels.length === 0) return reason;
  const reserved =
    sentinels.length === 1
      ? `Id ${sentinels[0]} is an engine sentinel and is never handed out`
      : `Ids ${sentinels.join(" and ")} are engine sentinels and are never handed out`;
  return `${reason} ${reserved}, so allocation starts at ${highestSafeId(spec)}.`;
}
